import { Search, Send, X } from "lucide-react";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Input } from "@/common/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/common/ui/select";
import { Skeleton } from "@/common/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/common/ui/tabs";

import { DebugSendModal } from "./DebugSendModal";
import { HealthStrip } from "./HealthStrip";
import { MessageDetailView } from "./MessageDetailView";
import type { MessageSummary } from "./messages-api";
import { fetchNamespaces, type NamespaceListItem } from "./namespaces-api";
import { type EnvConfig, type EnvName, getConfiguredEnvs } from "./test-inbox-config";
import {
  DEFAULT_FILTERS,
  type SearchFilters,
  type SearchTimePreset,
  useSearchMessages,
} from "./use-search-messages";

const TIME_PRESET_OPTIONS: { value: SearchTimePreset; label: string }[] = [
  { value: "1h", label: "Last hour" },
  { value: "24h", label: "Last 24 hours" },
  { value: "7d", label: "Last 7 days" },
  { value: "custom", label: "Custom range…" },
];

// Default landing window: 24h of messages so the inbox shows recent activity
// without forcing the user to type a search first.
const DEFAULT_INBOX_FILTERS: SearchFilters = {
  ...DEFAULT_FILTERS,
  timePreset: "24h",
};

function formatRelative(iso: string): string {
  const ms = Math.max(0, Date.now() - new Date(iso).getTime());
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function formatSender(addrs: { email: string; name: string | null }[]): string {
  if (addrs.length === 0) return "—";
  const first = addrs[0];
  if (!first) return "—";
  return first.name ?? first.email;
}

export function TestInboxPage(): React.ReactElement {
  const navigate = useNavigate();
  const { env: envParam } = useParams<{ env: string }>();
  const envs = useMemo(() => getConfiguredEnvs(), []);

  // Redirect when the URL doesn't name a configured env. Covers two cases:
  //   - bare `/test-inbox` (envParam is undefined)
  //   - `/test-inbox/<unknown>` (envParam present but not in the configured set)
  // In both cases redirect to the first configured env so the address bar
  // stays in sync with the rendered data and copy/paste URLs work.
  useEffect(() => {
    const first = envs[0];
    if (!first) return;
    const match = envs.some((e) => e.env === envParam);
    if (!match) navigate(`/test-inbox/${first.env}`, { replace: true });
  }, [envParam, envs, navigate]);

  if (envs.length === 0) {
    return (
      <div data-testid="test-inbox-page" className="space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">Test Inbox</h1>
        <p className="text-sm text-muted-foreground">
          No test-inbox env configured. Set one of <code>VITE_TEST_INBOX_URL_DEV</code>,
          <code> VITE_TEST_INBOX_URL_INTEGRATION</code>, or
          <code> VITE_TEST_INBOX_URL_STAGING</code> to enable this page.
        </p>
      </div>
    );
  }

  const selectedEnv = envs.find((e) => e.env === envParam) ?? envs[0];
  if (!selectedEnv) return <></>;

  return (
    <div data-testid="test-inbox-page" className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Test Inbox</h1>
        <Tabs
          data-testid="test-inbox-env-tabs"
          value={selectedEnv.env}
          onValueChange={(v) => navigate(`/test-inbox/${v}`)}
        >
          <TabsList>
            {envs.map((e) => (
              <TabsTrigger
                key={e.env}
                value={e.env}
                data-testid={`env-tab-${e.env}`}
                className="capitalize"
              >
                {e.env}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </header>

      <HealthStrip baseUrl={selectedEnv.baseUrl} env={selectedEnv.env} />

      <InboxContent key={selectedEnv.env} env={selectedEnv} />
    </div>
  );
}

interface InboxContentProps {
  env: EnvConfig;
}

function InboxContent({ env }: InboxContentProps): React.ReactElement {
  const { baseUrl } = env;
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMessageId = searchParams.get("messageId");
  const selectedNamespaceId = searchParams.get("ns");

  const {
    filters,
    setFilters,
    items,
    isLoading,
    error,
    hasMore,
    search,
    loadNextPage,
    clearFilters,
  } = useSearchMessages(baseUrl);

  // Initialize with the 24h default once per env switch (the outer `key`
  // remounts this component on env change, so this fires once per env).
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setFilters(DEFAULT_INBOX_FILTERS);
  }, [setFilters]);

  // Refetch whenever a non-text filter changes. Free-text subject search is
  // debounced separately (see `SearchInput` below) to avoid a server hit on
  // every keystroke.
  useEffect(() => {
    if (filters.timePreset === "none") return;
    search();
  }, [
    filters.timePreset,
    filters.customFrom,
    filters.customTo,
    filters.fromSender,
    filters.subjectContains,
    search,
  ]);

  const handleSelectMessage = useCallback(
    (m: MessageSummary): void => {
      const next = new URLSearchParams(searchParams);
      next.set("messageId", m.id);
      next.set("ns", m.namespaceId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDetail = useCallback((): void => {
    const next = new URLSearchParams(searchParams);
    next.delete("messageId");
    next.delete("ns");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // For debug-send: needs a target namespace. If the user has narrowed to
  // one, use it; otherwise the button is disabled until they pick one.
  const debugSendNamespaceId = selectedNamespaceId ?? null;

  const refresh = useCallback((): Promise<void> => search(), [search]);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <div className="space-y-3" data-testid="inbox-list-pane">
        <InboxToolbar
          filters={filters}
          setFilters={setFilters}
          onClearFilters={clearFilters}
          baseUrl={baseUrl}
          envName={env.env}
          onSelectNamespace={(nsId) => {
            const next = new URLSearchParams(searchParams);
            if (nsId) next.set("ns", nsId);
            else next.delete("ns");
            setSearchParams(next, { replace: true });
          }}
          selectedNamespaceId={selectedNamespaceId}
          debugSendNamespaceId={debugSendNamespaceId}
          refresh={refresh}
          items={items}
          onDebugSendFound={(id) => {
            const next = new URLSearchParams(searchParams);
            next.set("messageId", id);
            if (debugSendNamespaceId) next.set("ns", debugSendNamespaceId);
            setSearchParams(next, { replace: true });
          }}
        />
        <MessagesList
          items={items}
          isLoading={isLoading}
          error={error}
          hasMore={hasMore}
          onLoadMore={loadNextPage}
          selectedMessageId={selectedMessageId}
          selectedNamespaceId={selectedNamespaceId}
          onSelect={handleSelectMessage}
        />
      </div>
      <div className="rounded-md border bg-card p-4" data-testid="inbox-detail-pane">
        {selectedMessageId && selectedNamespaceId ? (
          <MessageDetailView
            baseUrl={baseUrl}
            namespaceId={selectedNamespaceId}
            messageId={selectedMessageId}
            onClose={handleCloseDetail}
          />
        ) : (
          <EmptyDetail />
        )}
      </div>
    </div>
  );
}

function EmptyDetail(): React.ReactElement {
  return (
    <div
      data-testid="inbox-detail-empty"
      className="flex h-full min-h-[20rem] flex-col items-center justify-center gap-2 text-center text-sm text-muted-foreground"
    >
      <p className="font-medium">No message selected</p>
      <p>Click a message in the list to view it here.</p>
    </div>
  );
}

interface InboxToolbarProps {
  filters: SearchFilters;
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
  onClearFilters: () => void;
  baseUrl: string;
  envName: EnvName;
  selectedNamespaceId: string | null;
  onSelectNamespace: (nsId: string | null) => void;
  debugSendNamespaceId: string | null;
  refresh: () => Promise<void>;
  items: MessageSummary[];
  onDebugSendFound: (id: string) => void;
}

function InboxToolbar({
  filters,
  setFilters,
  onClearFilters,
  baseUrl,
  envName,
  selectedNamespaceId,
  onSelectNamespace,
  debugSendNamespaceId,
  refresh,
  items,
  onDebugSendFound,
}: InboxToolbarProps): React.ReactElement {
  const [debugOpen, setDebugOpen] = useState(false);
  const filtersActive =
    filters.fromSender !== "" ||
    filters.subjectContains !== "" ||
    filters.timePreset !== "24h" ||
    selectedNamespaceId !== null;

  return (
    <div className="space-y-3 rounded-md border bg-card p-3" data-testid="inbox-toolbar">
      <SearchInput
        value={filters.subjectContains}
        onChange={(v) => setFilters((prev) => ({ ...prev, subjectContains: v }))}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={filters.timePreset === "none" ? "24h" : filters.timePreset}
          onValueChange={(v) =>
            setFilters((prev) => ({ ...prev, timePreset: v as SearchTimePreset }))
          }
        >
          <SelectTrigger
            data-testid="inbox-time-range"
            aria-label="Time range"
            className="h-8 w-[10rem] text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIME_PRESET_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <NamespaceFilterChip
          baseUrl={baseUrl}
          envName={envName}
          selectedNamespaceId={selectedNamespaceId}
          onSelect={onSelectNamespace}
        />

        <Input
          value={filters.fromSender}
          onChange={(e) => setFilters((prev) => ({ ...prev, fromSender: e.target.value }))}
          placeholder="From sender contains…"
          aria-label="From sender"
          data-testid="inbox-from-sender"
          className="h-8 w-[14rem] text-xs"
        />

        {filtersActive && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onClearFilters();
              onSelectNamespace(null);
              setFilters(DEFAULT_INBOX_FILTERS);
            }}
            data-testid="inbox-clear-filters"
            className="h-8 px-2 text-xs"
          >
            Clear
          </Button>
        )}

        <div className="ml-auto">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!debugSendNamespaceId}
            onClick={() => setDebugOpen(true)}
            data-testid="inbox-debug-send"
            title={
              debugSendNamespaceId
                ? "Open debug-send for the selected namespace"
                : "Pick a namespace filter to enable debug-send"
            }
            className="h-8 gap-1 px-2 text-xs"
          >
            <Send className="h-3 w-3" />
            Debug send
          </Button>
        </div>
      </div>

      {filters.timePreset === "custom" && (
        <CustomRangeInputs
          customFrom={filters.customFrom}
          customTo={filters.customTo}
          onChange={(from, to) =>
            setFilters((prev) => ({ ...prev, customFrom: from, customTo: to }))
          }
        />
      )}

      {debugSendNamespaceId && (
        <DebugSendModal
          open={debugOpen}
          onClose={() => setDebugOpen(false)}
          baseUrl={baseUrl}
          namespaceId={debugSendNamespaceId}
          items={items}
          refresh={refresh}
          onFound={(id) => {
            setDebugOpen(false);
            onDebugSendFound(id);
          }}
        />
      )}
    </div>
  );
}

interface SearchInputProps {
  value: string;
  onChange: (next: string) => void;
}

function SearchInput({ value, onChange }: SearchInputProps): React.ReactElement {
  // Local state lets the user type without the parent fan-out causing a
  // refetch per keystroke. Debounced through to the parent at 250 ms.
  const [local, setLocal] = useState(value);
  const debouncedRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => {
    return () => {
      if (debouncedRef.current !== undefined) window.clearTimeout(debouncedRef.current);
    };
  }, []);

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input
        type="search"
        value={local}
        onChange={(e) => {
          const next = e.target.value;
          setLocal(next);
          if (debouncedRef.current !== undefined) window.clearTimeout(debouncedRef.current);
          debouncedRef.current = window.setTimeout(() => onChange(next), 250);
        }}
        placeholder="Search subject…"
        aria-label="Search subject"
        data-testid="inbox-search-input"
        className="pl-8"
      />
    </div>
  );
}

interface CustomRangeProps {
  customFrom: string;
  customTo: string;
  onChange: (from: string, to: string) => void;
}

function CustomRangeInputs({
  customFrom,
  customTo,
  onChange,
}: CustomRangeProps): React.ReactElement {
  return (
    <div
      data-testid="inbox-custom-range-inputs"
      className="flex flex-wrap items-end gap-3 rounded-md bg-muted/30 p-2 text-xs"
    >
      <label className="flex flex-col gap-1">
        <span className="text-muted-foreground">From</span>
        <Input
          type="datetime-local"
          value={customFrom}
          onChange={(e) => onChange(e.target.value, customTo)}
          aria-label="Custom range from"
          data-testid="inbox-custom-from"
          className="h-7 text-xs"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-muted-foreground">To</span>
        <Input
          type="datetime-local"
          value={customTo}
          onChange={(e) => onChange(customFrom, e.target.value)}
          aria-label="Custom range to"
          data-testid="inbox-custom-to"
          className="h-7 text-xs"
        />
      </label>
    </div>
  );
}

interface NamespaceFilterChipProps {
  baseUrl: string;
  envName: EnvName;
  selectedNamespaceId: string | null;
  onSelect: (nsId: string | null) => void;
}

function NamespaceFilterChip({
  baseUrl,
  envName,
  selectedNamespaceId,
  onSelect,
}: NamespaceFilterChipProps): React.ReactElement {
  const [namespaces, setNamespaces] = useState<NamespaceListItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // In-flight guard: `loaded` only flips after the fetch resolves, so a fast
  // open/close/open cycle would otherwise fan out concurrent requests. The
  // ref gates re-entry even before the first response lands.
  const loadingRef = useRef(false);

  useEffect(() => {
    setLoaded(false);
    setNamespaces([]);
    loadingRef.current = false;
  }, [envName]);

  const loadNamespaces = useCallback(async (): Promise<void> => {
    if (loaded || loadingRef.current) return;
    loadingRef.current = true;
    try {
      const result = await fetchNamespaces(baseUrl, { sort: "lastReceivedAt", limit: 50 });
      setNamespaces(result.items);
    } catch {
      // Best-effort; if it fails the dropdown stays empty and the user can
      // still close it and rely on free-text search instead.
    } finally {
      setLoaded(true);
      loadingRef.current = false;
    }
  }, [baseUrl, loaded]);

  if (selectedNamespaceId) {
    return (
      <Badge
        variant="secondary"
        data-testid="inbox-namespace-chip"
        className="h-8 gap-1 px-2 font-mono text-xs"
      >
        <span className="truncate max-w-[10rem]">{selectedNamespaceId}</span>
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-label="Clear namespace filter"
          data-testid="inbox-namespace-clear"
          className="inline-flex items-center justify-center rounded hover:bg-muted-foreground/20"
        >
          <X className="h-3 w-3" />
        </button>
      </Badge>
    );
  }

  return (
    <Select
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) void loadNamespaces();
      }}
      value=""
      onValueChange={(v) => onSelect(v || null)}
    >
      <SelectTrigger
        data-testid="inbox-namespace-select"
        aria-label="Filter by namespace"
        className="h-8 w-[12rem] text-xs"
      >
        <SelectValue placeholder="All namespaces" />
      </SelectTrigger>
      <SelectContent>
        {namespaces.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            {loaded ? "No namespaces" : "Loading…"}
          </div>
        ) : (
          namespaces.map((ns) => (
            <SelectItem key={ns.id} value={ns.id} className="font-mono text-xs">
              {ns.id}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
}

interface MessagesListProps {
  items: MessageSummary[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  selectedMessageId: string | null;
  selectedNamespaceId: string | null;
  onSelect: (m: MessageSummary) => void;
}

function MessagesList({
  items,
  isLoading,
  error,
  hasMore,
  onLoadMore,
  selectedMessageId,
  onSelect,
}: MessagesListProps): React.ReactElement {
  return (
    <div data-testid="inbox-messages-list" className="overflow-hidden rounded-md border bg-card">
      {error && (
        <p data-testid="inbox-messages-error" className="border-b p-3 text-sm text-destructive">
          {error}
        </p>
      )}
      <ul className="max-h-[60vh] divide-y overflow-y-auto">
        {isLoading && items.length === 0 && (
          <>
            {[0, 1, 2].map((i) => (
              <li key={`skel-${i}`} data-testid="inbox-skeleton-row" className="p-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="mt-2 h-3 w-1/2" />
              </li>
            ))}
          </>
        )}

        {!isLoading && items.length === 0 && (
          <li
            data-testid="inbox-empty-row"
            className="p-6 text-center text-sm text-muted-foreground"
          >
            No messages match your filters.
          </li>
        )}

        {items.map((m) => {
          const selected = m.id === selectedMessageId;
          return (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => onSelect(m)}
                data-testid={`inbox-message-row-${m.id}`}
                aria-current={selected ? "true" : undefined}
                className={`flex w-full flex-col items-start gap-1 px-3 py-2 text-left transition hover:bg-muted/50 focus:bg-muted/50 ${
                  selected ? "bg-muted" : ""
                }`}
              >
                <div className="flex w-full items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium">{formatSender(m.from)}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatRelative(m.receivedAt)}
                  </span>
                </div>
                <span className="line-clamp-1 text-sm text-muted-foreground">
                  {m.subject ?? <span className="italic">(no subject)</span>}
                </span>
                <span className="font-mono text-[10px] text-muted-foreground/70">
                  {m.namespaceId}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {hasMore && (
        <div className="border-t p-2 text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLoadMore}
            disabled={isLoading}
            data-testid="inbox-load-more"
            className="h-7 text-xs"
          >
            {isLoading ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
